import { RegionAppPath } from "@gap/utils/region-utils";
import { Location as RouterLocation } from "react-router-dom";
export const ULID_REGEX = /[0-7][0-9A-HJKMNP-TV-Z]{25}/gm

export const extractUrlRegion = (location: Location | RouterLocation) => {
   // Match our expected pattern, but fall back to the full pathname and empty region
   const parts = location.pathname.match(/^\/([a-z]{2})(\/.*)?$/i) || [undefined, undefined, location.pathname]
   const region = parts[1] as `${RegionAppPath}`
   const path = parts[2] || '/'
   return { region, path }
}

export const redactUlidsFromPath = (path: string) => {
   return path.replaceAll(ULID_REGEX, '<id>')
}

/** Written in place of a dropped value, so a reader can still see the parameter was present. */
const REDACTED = '[redacted]'

/**
 * Query parameter names whose value is a credential rather than something you debug with. Matched
 * as a substring so `access_token` and `client_secret` are both caught; `code` and `state` are
 * anchored, since they are only credentials as whole names.
 */
const CREDENTIAL_QUERY_PARAM_PATTERN = /secret|passwd|password|token|credential|authcode|apikey|api_key|^code$|^state$/i

/**
 * Redacts credential-bearing query parameters from a URL, absolute or relative, leaving the path
 * and every other parameter intact. Returns the input unchanged when it carries no query string.
 */
export const redactCredentialQueryParams = (url: string): string => {
   const queryStart = url.indexOf('?')
   if (queryStart === -1) return url

   const path = url.slice(0, queryStart)
   const afterQuestionMark = url.slice(queryStart + 1)
   const hashStart = afterQuestionMark.indexOf('#')
   const search = hashStart === -1 ? afterQuestionMark : afterQuestionMark.slice(0, hashStart)
   const hash = hashStart === -1 ? '' : afterQuestionMark.slice(hashStart)

   const redacted = new URLSearchParams()
   for (const [parameter, value] of new URLSearchParams(search)) {
      redacted.append(parameter, CREDENTIAL_QUERY_PARAM_PATTERN.test(parameter) ? REDACTED : value)
   }
   return `${path}?${redacted.toString()}${hash}`
}

const RECORDING_URL_KEYS = ['href', 'url', 'from', 'to'] as const

/**
 * Redacts credential-bearing URLs on a Session Replay recording event. Only walks `href` / `url` /
 * `from` / `to` (and the same keys nested under `data` / `payload`) so a full DOM snapshot is not
 * traversed.
 */
export const redactCredentialUrlsInRecordingEvent = <T extends object>(event: T): T => {
   redactHrefFields(event as Record<string, unknown>)
   return event
}

function redactHrefFields(record: Record<string, unknown>): void {
   for (const key of RECORDING_URL_KEYS) {
      const value = record[key]
      if (typeof value === 'string') record[key] = redactCredentialQueryParams(value)
   }
   const nestedData = record.data
   if (nestedData && typeof nestedData === 'object') redactHrefFields(nestedData as Record<string, unknown>)
   const payload = record.payload
   if (payload && typeof payload === 'object') redactHrefFields(payload as Record<string, unknown>)
}

/**
 * Redacts credential-bearing URLs on a Sentry Replay event payload. The envelope's `urls` list is
 * written in `setInitialState` and is not a recording frame, so `beforeAddRecordingEvent` never
 * sees it — this is the hook that does.
 */
export const redactCredentialUrlsInReplayEvent = (event: { urls?: unknown; request?: { url?: unknown } }): void => {
   if (Array.isArray(event.urls)) {
      event.urls = event.urls.map(url => (typeof url === 'string' ? redactCredentialQueryParams(url) : url))
   }
   if (event.request && typeof event.request.url === 'string') {
      event.request = { ...event.request, url: redactCredentialQueryParams(event.request.url) }
   }
}
